// PDF generation utilities using jsPDF
export const generatePDF = (content, filename) => {
  // For now, we'll create a text file as a placeholder
  // In a real implementation, you would use jsPDF library
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const generateSpecificationsPDF = (data) => {
  const projectName = data.projectName || 'Project';
  const date = new Date().toLocaleDateString();
  
  const content = `C3030 CEILING ASSEMBLY SPECIFICATIONS
Generated: ${date}

PROJECT INFORMATION
===================
Project Name: ${data.projectName || 'Not specified'}
Project Owner: ${data.owner || 'Not specified'}
Occupancy: ${data.occupancy || 'Not specified'}
Start Date: ${data.startDate || 'Not specified'}
Location: ${data.location || 'Not specified'}
Room Type: ${data.roomType || 'Not specified'}
Area (sqm): ${data.areaSqm || 'Not specified'}
Issue Date: ${date}

ELEMENT SPECIFICATIONS
=====================

Light Gauge Steel Framing (LGS framing members for suspended or framed ceilings)
--------------------------------------------------------------------------------
Hanger with Clips and Brackets: Solid rod, threaded rod, or suspension wire; drop lengths up to 300 mm, 301–600 mm, or 601–900 mm. 
Wall Angle: Single or double (shadow gap); thickness 0.6–1.0 mm. 
Carrying Channel: Thickness 0.6–1.0 mm. 
Furring: Single or double; thickness 0.4–0.6 mm. 
Clips: W-clip or wire coupling clip.

Ceiling Surface & Accessories (primary visible finish layer)
------------------------------------------------------------
Painted Fiber Cement Board: Factory-finished; corrosion-resistant fasteners; installed to manufacturer's specifications. 
Fiber Cement Board Screws (32–45 mm): Corrosion-resistant; automatically selected with painted fiber cement. 
Painted Gypsum Board: Factory-finished; installed to manufacturer's specifications. 
Gypsum Board Screws (25–32 mm): Black phosphate; automatically selected with painted gypsum board. 
PVC Ceiling Panels: As specified; moisture-resistant and washable surface.

Ceiling Finish – Paints and Coatings (final decorative and protective treatment)
--------------------------------------------------------------------------------
Jointing Tape: As selected, with main and two alternate approved brands. 
Jointing Putty: As selected, with main and two alternate approved brands. 
Paint System: Interior-grade paint, compliant with Master Painters Institute (MPI) standards for substrate type and gloss level. 
All coats from a single manufacturer; VOC limits per MPI GPS-1-12.

Supplementary – Interior Coatings (from C3040)
----------------------------------------------
Performance Standard: All paints to be from the MPI "Approved Product List" current at contract award, or more recent if used consistently for entire project. 
All coats in a system from a single manufacturer. 
System Selection: Use MPI Architectural Painting Decision Tree for environmental condition and substrate type. 
Select "Aggressive" conditions for humid, abrasive, or chemical-exposed interiors such as bathrooms, kitchens, pools, laundry, and similar. 

Gloss Levels: 
G2 "Velvet-like" Flat – ceilings, low-traffic walls. 
G3 "Eggshell-like" – high-traffic ceilings/walls, limited human contact. 
G5 Semi-gloss – walls, doors, trim. 
G6 Gloss – special conditions. 

Surface Preparation: Comply with MPI "Interior Surface Preparation" or "Maintenance Repainting" manuals. 
Clean surfaces to remove contaminants. Ferrous metals in aggressive environments to be SSPC SP 10 (near white) cleaned before priming. 

VOC Limits: Interior flat coats max 50 g/L; non-flat coats max 150 g/L VOC. Prefer MPI VOC Range E3 where possible. 

Additional Coatings: Where specified, use high-performance architectural coatings (epoxy-polyamide or moisture-curing urethane) in areas with high abrasion, moisture, or chemical exposure.

---
Specification generated based on selected materials and configurations from Part II`;

  generatePDF(content, `${projectName.replace(/\s+/g, '_')}_specifications.pdf`);
};

export const generateCostEstimatePDF = (data, costData, descriptions) => {
  const projectName = data.projectName || 'Project';
  const date = new Date().toLocaleDateString();
  const totalCost = costData.reduce((sum, item) => sum + item.totalCost, 0);
  
  let content = `RESOURCE AND COST ESTIMATE
Generated: ${date}
Project: ${projectName}

DETAILED COST BREAKDOWN
======================

A. LGS Framing
--------------
`;

  // Add LGS items
  costData.slice(0, 5).forEach(item => {
    content += `${item.mfCode} - ${item.description}
Description: ${descriptions[item.description.toLowerCase().replace(/\s+/g, '')] || 'Not specified'}
Quantity: ${item.quantity} ${item.unit}
Material Cost: ₱${item.materialUnitCost.toLocaleString()} per ${item.unit}
Labor Cost: ₱${item.laborUnitCost.toLocaleString()} per ${item.unit}
Total Unit Cost: ₱${item.totalUnitCost.toLocaleString()}
Total Cost: ₱${item.totalCost.toLocaleString()}

`;
  });

  content += `B. Ceiling Surface & Accessories
-------------------------------
`;

  // Add surface items
  costData.slice(5, 6).forEach(item => {
    content += `${item.mfCode} - ${item.description}
Description: ${descriptions.ceilingsurface || 'Not specified'}
Quantity: ${item.quantity} ${item.unit}
Material Cost: ₱${item.materialUnitCost.toLocaleString()} per ${item.unit}
Labor Cost: ₱${item.laborUnitCost.toLocaleString()} per ${item.unit}
Total Unit Cost: ₱${item.totalUnitCost.toLocaleString()}
Total Cost: ₱${item.totalCost.toLocaleString()}

`;
  });

  content += `C. Ceiling Finish (Paints and Coatings)
--------------------------------------
`;

  // Add finish items
  costData.slice(6).forEach(item => {
    content += `${item.mfCode} - ${item.description}
Description: ${item.description.includes('Jointing') ? descriptions.jointingtape || 'Not specified' : descriptions.paint || 'Not specified'}
Quantity: ${item.quantity} ${item.unit}
Material Cost: ₱${item.materialUnitCost.toLocaleString()} per ${item.unit}
Labor Cost: ₱${item.laborUnitCost.toLocaleString()} per ${item.unit}
Total Unit Cost: ₱${item.totalUnitCost.toLocaleString()}
Total Cost: ₱${item.totalCost.toLocaleString()}

`;
  });

  content += `
TOTAL PROJECT COST: ₱${totalCost.toLocaleString()}

---
Cost estimate based on current market rates and selected specifications.`;

  generatePDF(content, `${projectName.replace(/\s+/g, '_')}_cost_estimate.pdf`);
};

export const generateSupplierInfoPDF = (data, supplierData) => {
  const projectName = data.projectName || 'Project';
  const date = new Date().toLocaleDateString();
  
  let content = `SUPPLIER & SOURCE INFORMATION
Generated: ${date}
Project: ${projectName}

SUPPLIER DIRECTORY
==================

`;

  supplierData.forEach((supplier, index) => {
    content += `${index + 1}. ${supplier.name}
${'='.repeat(supplier.name.length + 3)}
Brand: ${supplier.brand}
Products: ${supplier.products}
Contact Person: ${supplier.contactPerson}
Designation: ${supplier.designation}
Contact Information:
${supplier.contactInfo.replace(/\n/g, '\n')}

`;
  });

  content += `---
Supplier information current as of ${date}. Please verify contact details before placing orders.`;

  generatePDF(content, `${projectName.replace(/\s+/g, '_')}_supplier_info.pdf`);
};

export const generateBillOfMaterialsPDF = (data, bomData) => {
  const projectName = data.projectName || 'Project';
  const date = new Date().toLocaleDateString();
  
  let content = `BILL OF MATERIALS / REQUEST FOR QUOTATION
Generated: ${date}
Project: ${projectName}

MATERIAL LIST
=============

`;

  bomData.forEach(item => {
    content += `☐ ${item.itemNo}. ${item.description}
   Supplier: ${item.supplier}
   Quantity: ${item.qty} ${item.uom}
   Unit Price: ${item.unitPrice || '___________'}
   Amount: ${item.amount || '___________'}

`;
  });

  content += `
TOTAL AMOUNT: ___________

INSTRUCTIONS
============
Please provide your best quotation for the above materials.
All materials should meet or exceed the specifications outlined in the project documents.
Delivery should be coordinated with the project schedule.

---
Request for quotation generated from BQ SyncSpec system.`;

  generatePDF(content, `${projectName.replace(/\s+/g, '_')}_bill_of_materials.pdf`);
};